import * as React from 'react';
import { IRemitLine } from '../../services/remits/remit-claims';
import { AdjustmentCodes, formatPayerAdjustmentGroupLabel } from '../../services/dictionary';
import { formatDate } from '../../services/primitives/date-utils';
import { money } from './remit-format';
import * as S from './remits.styles';

/** `Sporleder, Ruby` → `RUBY SPORLEDER`, as the popup heads it (design 03). */
const headline = (patient: string): string => patient.split(', ').reverse().join(' ').toUpperCase();

/**
 * One service line, opened from its row in a claim remit (design 03). Everything here is the line itself; the only
 * thing added is each adjustment's CARC text, read from the dictionary (crosswalk §2, "Service line modal").
 */
export const RemitLineDialog: React.FC<{ line: IRemitLine; patient?: string; memberId?: string }> = ({
    line,
    patient,
    memberId,
}) => (
    <S.LineDialog>
        {(patient || memberId) && (
            <S.LineHead>
                {patient && <h3>{headline(patient)}</h3>}
                {memberId && <S.Muted>{memberId}</S.Muted>}
            </S.LineHead>
        )}
        <S.LineFacts>
            <div>
                <dt>Procedure Code</dt>
                <dd>{line.procedureCode || '—'}</dd>
            </div>
            <div>
                <dt>Units</dt>
                <dd>{line.units ?? '—'}</dd>
            </div>
            <div>
                <dt>From date of service</dt>
                <dd>
                    {formatDate(line.fromDos, 'MMM dd, yyyy')}
                    {line.throughDos && line.throughDos !== line.fromDos
                        ? ` – ${formatDate(line.throughDos, 'MMM dd, yyyy')}`
                        : ''}
                </dd>
            </div>
            <div>
                <dt>Charge Amount</dt>
                <dd>{money(line.billed)}</dd>
            </div>
            <div>
                <dt>Paid Amount</dt>
                <dd>{money(line.paid)}</dd>
            </div>
            <div>
                <dt>Modifiers</dt>
                <dd>{line.modifiers.length ? line.modifiers.join(', ') : '—'}</dd>
            </div>
        </S.LineFacts>
        <S.SectionHead as="div">
            <span>Adjustments</span>
        </S.SectionHead>
        {!line.adjustments.length && <S.Muted>None on this line.</S.Muted>}
        {line.adjustments.map((adjustment, index) => (
            <S.AdjustmentCard key={`${adjustment.group}-${adjustment.code}-${index}`}>
                <header>
                    <span>[{adjustment.code}]</span>
                    <span>{money(adjustment.amount)}</span>
                </header>
                <p>{(AdjustmentCodes as Record<string, string>)[adjustment.code] || 'No description for this code in the CARC list.'}</p>
                <small>{formatPayerAdjustmentGroupLabel(adjustment.group).toUpperCase()}</small>
            </S.AdjustmentCard>
        ))}
    </S.LineDialog>
);
